import * as THREE from 'three'

import { WORLD_Z_PER_Y } from '../../shared/shared'

const DEFAULT_FPS = 4
const MAX_SPRITES = 512

export class ClientSprites {
  constructor(client) {
    this.client = client
    this.atlas = client.spriteAtlas
    this.byId = new Map()
    this.nextId = 0
    this._freeSlots = []
    this._highWater = 0

    const geo = new THREE.PlaneGeometry(1, 1)
    // no translate — pivot is controlled per-instance via aYOffset so yCenter rotates correctly

    const uvArr = new Float32Array(MAX_SPRITES * 4)
    const animArr = new Float32Array(MAX_SPRITES * 3) // frameCount, fps, strideU
    const startTimeArr = new Float32Array(MAX_SPRITES).fill(-1)
    const sortOffsetArr = new Float32Array(MAX_SPRITES)
    const yOffsetArr = new Float32Array(MAX_SPRITES)
    const rotationArr = new Float32Array(MAX_SPRITES)

    geo.setAttribute('aUvRect', new THREE.InstancedBufferAttribute(uvArr, 4))
    geo.setAttribute('aAnimData', new THREE.InstancedBufferAttribute(animArr, 3))
    geo.setAttribute('aStartTime', new THREE.InstancedBufferAttribute(startTimeArr, 1))
    geo.setAttribute('aSortOffset', new THREE.InstancedBufferAttribute(sortOffsetArr, 1))
    geo.setAttribute('aYOffset', new THREE.InstancedBufferAttribute(yOffsetArr, 1))
    geo.setAttribute('aRotation', new THREE.InstancedBufferAttribute(rotationArr, 1))

    this._uvArr = uvArr
    this._animArr = animArr
    this._startTimeArr = startTimeArr
    this._sortOffsetArr = sortOffsetArr
    this._yOffsetArr = yOffsetArr
    this._rotationArr = rotationArr
    this._dummy = new THREE.Object3D()

    const mat = this._createMaterial()
    this.mesh = new THREE.InstancedMesh(geo, mat, MAX_SPRITES)
    this.mesh.frustumCulled = false
    this.mesh.count = 0
    this.client.scene.add(this.mesh)

    this.atlas.onReady(() => {
      this.mesh.material.uniforms.uAtlas.value = this.atlas.texture
    })
  }

  _createMaterial() {
    return new THREE.ShaderMaterial({
      uniforms: {
        uAtlas: { value: this.atlas.texture },
        uTime: { value: 0 },
        uAlphaTest: { value: 0.5 },
        uCameraY: this.client.uniforms.uCameraY,
        uWorldZPerY: { value: WORLD_Z_PER_Y },
        ...this.client.lights.uniforms,
      },
      vertexShader: /* glsl */ `
        precision highp float;
        attribute vec4 aUvRect;
        attribute vec3 aAnimData;
        attribute float aStartTime;
        attribute float aSortOffset;
        attribute float aYOffset;
        attribute float aRotation;
        uniform float uTime;
        uniform float uCameraY;
        uniform float uWorldZPerY;
        varying vec2 vUv;
        varying vec2 vWorldPos;
        void main() {
          float frameCount = aAnimData.x;
          float fps = aAnimData.y;
          float strideU = aAnimData.z;
          float frame = 0.0;
          if (frameCount > 1.0 && fps > 0.0) {
            if (aStartTime < 0.0) {
              frame = floor(mod(uTime * fps, frameCount));
            } else {
              frame = min(floor((uTime - aStartTime) * fps), frameCount - 1.0);
            }
          }
          vec2 mn = vec2(aUvRect.x + frame * strideU, aUvRect.y);
          vUv = mix(mn, mn + aUvRect.zw, uv);

          // scale → shift pivot → rotate → translate
          // aYOffset = h/2 - yCenter, applied before rotation so yCenter is the rotation pivot
          vec2 scaled = position.xy * vec2(instanceMatrix[0][0], instanceMatrix[1][1]);
          scaled.y += aYOffset;
          float c = cos(aRotation);
          float s = sin(aRotation);
          vec2 rotated = vec2(scaled.x * c - scaled.y * s, scaled.x * s + scaled.y * c);
          vec4 worldPos = vec4(instanceMatrix[3].xy + rotated, 0.0, 1.0);

          float pivotY = instanceMatrix[3].y + aSortOffset;
          float sortZ = (uCameraY - pivotY) * uWorldZPerY;
          vWorldPos = worldPos.xy;
          gl_Position = projectionMatrix * viewMatrix * vec4(worldPos.xy, sortZ, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform sampler2D uAtlas;
        uniform float uAlphaTest;
        varying vec2 vUv;
        varying vec2 vWorldPos;
        ${this.client.lights.UNIFORMS_GLSL}
        ${this.client.lights.FUNCTION_GLSL}
        void main() {
          vec4 c = texture2D(uAtlas, vUv);
          if (c.a < uAlphaTest) discard;
          c.rgb = applyLighting(c.rgb, vWorldPos);
          gl_FragColor = c;
        }
      `,
      transparent: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide,
    })
  }

  _allocSlot() {
    if (this._freeSlots.length > 0) return this._freeSlots.pop()
    if (this._highWater < MAX_SPRITES) {
      const slot = this._highWater++
      this.mesh.count = this._highWater
      return slot
    }
    return -1
  }

  _freeSlot(slot) {
    this._dummy.scale.set(0, 0, 0)
    this._dummy.updateMatrix()
    this.mesh.setMatrixAt(slot, this._dummy.matrix)
    this.mesh.instanceMatrix.needsUpdate = true
    this._dummy.scale.set(1, 1, 1)
    this._freeSlots.push(slot)
  }

  add(opts) {
    const slot = this._allocSlot()
    if (slot === -1) return null
    const id = this.nextId++
    const sprite = new Sprite(this, id, slot, opts)
    this.byId.set(id, sprite)
    return sprite
  }

  update(delta, time) {
    this.mesh.material.uniforms.uTime.value = time
  }

  destroy() {
    for (const sprite of this.byId.values()) {
      sprite.destroy()
    }
    this.client.scene.remove(this.mesh)
    this.mesh.geometry.dispose()
    this.mesh.material.dispose()
  }
}

class Sprite {
  constructor(sprites, id, slot, opts) {
    this.sprites = sprites
    this.client = sprites.client
    this.atlas = sprites.atlas
    this.id = id
    this.slot = slot
    this._w = opts.width || 1
    this._h = opts.height || 1
    this._x = opts.x || 0
    this._y = opts.y || 0
    this.fps = opts.fps
    this.url = opts.url
    this._loop = opts.loop !== false
    this._onEnd = opts.onEnd || null
    this._oneShot = null
    this._wobble = null
    this._baseFps = null

    const yCenter = opts.yCenter || 0
    const yBase = opts.yBase || 0
    sprites._sortOffsetArr[slot] = opts.ySort !== undefined ? opts.ySort : yBase - yCenter
    sprites._yOffsetArr[slot] = this._h / 2 - yCenter
    sprites._startTimeArr[slot] = -1
    sprites._rotationArr[slot] = 0
    sprites.mesh.geometry.attributes.aSortOffset.needsUpdate = true
    sprites.mesh.geometry.attributes.aYOffset.needsUpdate = true
    sprites.mesh.geometry.attributes.aStartTime.needsUpdate = true
    sprites.mesh.geometry.attributes.aRotation.needsUpdate = true

    this._updateMatrix()

    this._onReady = url => {
      if (url === this.url) this._updateUVs()
    }
    this.atlas.onReady(this._onReady)

    if (opts.url) {
      this._resolve(opts.url)
    }
  }

  _resolve(url) {
    this.atlas.resolve(url, this._w, this._h)
    this._updateUVs()
  }

  _updateMatrix() {
    const d = this.sprites._dummy
    d.position.set(this._x, this._y, 0)
    d.scale.set(this._w, this._h, 1)
    d.updateMatrix()
    this.sprites.mesh.setMatrixAt(this.slot, d.matrix)
    this.sprites.mesh.instanceMatrix.needsUpdate = true
  }

  _updateUVs() {
    const uv = this.atlas.getUV(this.url)
    if (!uv) return

    const fps = this.fps != null ? this.fps : uv.frameCount > 1 ? DEFAULT_FPS : 0
    const i4 = this.slot * 4
    this.sprites._uvArr[i4] = uv.u
    this.sprites._uvArr[i4 + 1] = uv.v
    this.sprites._uvArr[i4 + 2] = uv.frameW
    this.sprites._uvArr[i4 + 3] = uv.frameH

    const i3 = this.slot * 3
    this.sprites._animArr[i3] = uv.frameCount
    this.sprites._animArr[i3 + 1] = fps
    this.sprites._animArr[i3 + 2] = uv.strideU

    this.sprites.mesh.geometry.attributes.aUvRect.needsUpdate = true
    this.sprites.mesh.geometry.attributes.aAnimData.needsUpdate = true

    if (!this._loop && uv.frameCount > 1 && fps > 0) {
      this.sprites._startTimeArr[this.slot] = this.sprites.mesh.material.uniforms.uTime.value
      this.sprites.mesh.geometry.attributes.aStartTime.needsUpdate = true
      this._oneShot = { elapsed: 0, duration: uv.frameCount / fps }
      this.client.setHot(this.update, true)
    }
  }

  setPosition(x, y) {
    this._x = x
    this._y = y
    this._updateMatrix()
  }

  setRotation(angle) {
    this.sprites._rotationArr[this.slot] = angle
    this.sprites.mesh.geometry.attributes.aRotation.needsUpdate = true
  }

  setAnimating(on) {
    if (this._baseFps === null) {
      this._baseFps = this.sprites._animArr[this.slot * 3 + 1]
    }
    this.sprites._animArr[this.slot * 3 + 1] = on ? this._baseFps : 0
    this.sprites.mesh.geometry.attributes.aAnimData.needsUpdate = true
  }

  wobble(duration = 0.6, angle = 0.06) {
    this._wobble = { duration, angle, elapsed: 0 }
    this.client.setHot(this.update, true)
  }

  update = delta => {
    if (this._oneShot) {
      this._oneShot.elapsed += delta
      if (this._oneShot.elapsed >= this._oneShot.duration) {
        this._oneShot = null
        this._onEnd?.()
      }
    }

    if (this._wobble) {
      this._wobble.elapsed += delta
      if (this._wobble.elapsed >= this._wobble.duration) {
        this._wobble = null
        this.setRotation(0)
      } else {
        const t = 1 - this._wobble.elapsed / this._wobble.duration
        this.setRotation(Math.sin(this._wobble.elapsed * 12) * this._wobble.angle * t)
      }
    }

    if (!this._oneShot && !this._wobble) {
      this.client.setHot(this.update, false)
    }
  }

  destroy() {
    this._oneShot = null
    this._wobble = null
    this.client.setHot(this.update, false)
    this.sprites._freeSlot(this.slot)
    this.sprites.byId.delete(this.id)
    this.atlas.offReady(this._onReady)
  }
}
